import React from 'react';

interface Props {
  refreshedAt: string | Date | null | undefined;
  /** Threshold (ms) below which the badge is "fresh" (accent). Default 10 min. */
  freshMs?: number;
  /** Threshold (ms) below which the badge is "warm" (outline). Default 60 min. */
  warmMs?: number;
}

function formatMinutes(ms: number): string {
  return `${Math.max(0, Math.round(ms / 60000))}m`;
}

function formatHours(ms: number): string {
  if (ms < 24 * 60 * 60 * 1000) return `${Math.round(ms / 3_600_000)}h`;
  return `${Math.round(ms / 86_400_000)}d`;
}

/**
 * Snapshot freshness badge — uses the design-system hf-tag with
 * accent / outline / warning variants per V2 design spec.
 *   < freshMs   → accent  "fresh · {m}m"
 *   < warmMs    → outline "{m}m"
 *   ≥ warmMs    → warning "stale · {h}h"
 */
const FreshBadge: React.FC<Props> = ({
  refreshedAt,
  freshMs = 10 * 60 * 1000,
  warmMs = 60 * 60 * 1000
}) => {
  if (!refreshedAt) {
    return <span className="hf-tag outline sm" title="no snapshot timestamp">snapshot · —</span>;
  }
  const ts = typeof refreshedAt === 'string' ? new Date(refreshedAt) : refreshedAt;
  const age = Date.now() - ts.getTime();
  if (age < freshMs) {
    return <span className="hf-tag accent sm" title={ts.toISOString()}>fresh · {formatMinutes(age)}</span>;
  }
  if (age < warmMs) {
    return <span className="hf-tag outline sm" title={ts.toISOString()}>{formatMinutes(age)}</span>;
  }
  return <span className="hf-tag warning sm" title={ts.toISOString()}>stale · {formatHours(age)}</span>;
};

export default FreshBadge;
